import React from 'react'
import { findDOMNode } from "react-dom"
import getSearchText from '../utils/getSearchText'

import {MentionSuggestionsProps} from './@types'
import {getActiveOffsetKey, getSelectionIsInsideWord} from './helpers'
import MentionSuggestionsDropdown from './dropdown'
import MentionSuggestionsPopover from './suggestions-popover'

export default class MentionSuggestions extends MentionSuggestionsDropdown {

    popover                     : HTMLDivElement
    activeOffsetKey             : string

    constructor(props:MentionSuggestionsProps) {
        super(props)
        this.props.callbacks.onChange = this.onEditorStateChange.bind(this)

        this._bind('onEditorStateChange', 'onSearchChange')
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown)
    }

    componentDidUpdate(prevProps) {
        if (this.popover) {
            // In case the list shrinks there should be still an option focused.
            // Note: this might run multiple times and deduct 1 until the condition is
            // not fullfilled anymore.
            const size = this.props.suggestions.length
            if (size > 0 && this.state.focusedOptionIndex >= size) {
                this.setState({
                    focusedOptionIndex: size - 1,
                })
            }

            // Note: this is a simple protection for the error when componentDidUpdate
            // try to get new getPortalClientRect, but the key already was deleted by
            // previous action. (right now, it only can happened when set the mention
            // trigger to be multi-characters which not supported anyway!)
            if (!this.props.store.getAllSearches().has(this.activeOffsetKey)) {
                return
            }

            const decoratorRect = this.props.store.getPortalClientRect(this.activeOffsetKey)
            const newStyles = this.props.positionSuggestions({
                decoratorRect,
                prevProps,
                props: this.props,
                popover: this.popover,
            })
            Object.keys(newStyles).forEach(key => {
                this.popover.style[key] = newStyles[key]
            })
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown)
        this.props.callbacks.onChange = undefined
    }

    onEditorStateChange(editorState) {
        const searches = this.props.store.getAllSearches()

        // if no search portal is active there is no need to show the popover
        if (searches.size === 0) {
            return editorState
        }

        const selectionIsInsideWord = getSelectionIsInsideWord(editorState, searches, this.props.mentionTrigger)
        if (!selectionIsInsideWord) {
            this.props.store.resetEscapedSearch()
            this.closeDropdown()
            return editorState
        }

        const lastActiveOffsetKey = this.activeOffsetKey
        this.activeOffsetKey = getActiveOffsetKey(selectionIsInsideWord)

        let suggestions = this.props.suggestions.slice()
        this.onSearchChange(
            editorState,
            editorState.getSelection(),
            this.activeOffsetKey,
            lastActiveOffsetKey,
            (filteredSuggestions)=>{suggestions = filteredSuggestions}
        )

        // make sure the escaped search is reseted in the cursor since the user
        // already switched to another mention search
        if (!this.props.store.isEscaped(this.activeOffsetKey)) {
            this.props.store.resetEscapedSearch()
        }

        // If none of the above triggered to close the window, it's safe to assume
        // the dropdown should be open. This is useful when a user focuses on another
        // input field and then comes back: the dropdown will show again.
        if (
            !this.state.isActive &&
            !this.props.store.isEscaped(this.activeOffsetKey) &&
            suggestions.length > 0
        ) {
            this.openDropdown()
        }

        // makes sure the focused index is reseted every time a new selection opens
        // or the selection was moved to another mention search
        if (
            this.lastSelectionIsInsideWord === undefined ||
            !selectionIsInsideWord.equals(this.lastSelectionIsInsideWord)
        ) {
            this.setState({
                focusedOptionIndex: 0,
            })
        }

        this.lastSelectionIsInsideWord = selectionIsInsideWord

        return editorState
    }

    onSearchChange(editorState, selection, activeOffsetKey, lastActiveOffsetKey, callback) {
        const { matchingString: searchValue } = getSearchText(editorState, selection, this.props.mentionTrigger)

        if (
            this.lastSearchValue !== searchValue ||
            activeOffsetKey !== lastActiveOffsetKey
        ) {
            this.lastSearchValue = searchValue
            this.props.onSearchChange(searchValue, callback)
        }
    }

    render() {
        if (!this.state.isActive) {
            return null
        }

        return (
            <div ref={element => { this.popover = element }}
                 className="mentions-popover-target">
                <MentionSuggestionsPopover scrollRef          = {this.scrollRef}
                                           suggestions        = {this.props.suggestions}
                                           focusedOptionIndex = {this.state.focusedOptionIndex}
                                           uniqKey            = {this.uniqKey}
                                           theme              = {this.props.theme}
                                           searchValue        = {this.lastSearchValue}
                                           entryComponent     = {this.props.entryComponent}
                                           getTargetFunc      = {()=>findDOMNode(this)}
                                           onMentionSelect    = {this.onMentionSelect}
                                           onMentionFocus     = {this.onMentionFocus}
                                           onClickOutside     = {()=>this.closeDropdown()} />
            </div>
        )
    }
}
